import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnalytics, useAppSelector, useBindDispatch } from 'hooks'
import { Spinner, TopBar, SearchableTab, DownloadVillageCard } from 'components'

import { colors } from 'styles/colors'
import {
    constants,
    getConnectUser,
    isFieldAgent,
    searchBy,
    setOpenedVillageSession,
    VillageType
} from 'helpers'
import { MenuSvgs, VeIcon } from 'assets'
import { VeType } from 'storeTypes/vesTypes'
import { DownloadVeCard } from 'components/card/DownloadVeCard'
import { useNavigate } from 'react-router-dom'

export const FcVillage = () => {
    const { VillagesIcon } = MenuSvgs
    const { t } = useTranslation()
    const fcData = useAppSelector((state) => state.fcMetrics.data)
    const navigate = useNavigate()

    const {
        ves,
        villages,
        loading: loadingVes
    } = useAppSelector((state) => state.ves)
    const connectUser = getConnectUser()
    const { staff_code: staffCode, user_id: userId } = connectUser
    const fieldAgent = isFieldAgent(connectUser.role)
    const {
        fetchVesHandler,
        fetchAssignedVillagesHandler,
        fcMetricsHandler,
        selectVeVillageHandler
    } = useBindDispatch()
    const { trackEvent } = useAnalytics()

    useEffect(() => {
        if (!fcData && staffCode) {
            fcMetricsHandler(staffCode)
        }
    }, [fcData, staffCode])

    useEffect(() => {
        if (fieldAgent) {
            if (userId) {
                fetchAssignedVillagesHandler(userId)
            }
            return
        }
        if (fcData?.fc_info?.zone_code) {
            fetchVesHandler(fcData.fc_info.zone_code)
        }
    }, [fcData, fieldAgent, userId])

    if (!staffCode || !fcData || loadingVes) {
        return <Spinner size="90" pageSpinner={true} />
    }

    const veOpenClickHandler = (ve: VeType) => {
        navigate(`/fc/village/ve/${ve.code}/profile?from_tab=1`)
    }

    const villageOpenClickHandler = (village: VillageType) => {
        // Open the village page (Producteurs + today's enrollments) rather
        // than the client list. veCode is cleared so the client-list back
        // button returns here. The client pages prefer the Redux village
        // list over localStorage, so both are set.
        const standaloneVillages = setOpenedVillageSession(village, {
            zoneName: fcData.fc_info.zone_name,
            zoneSFID: fcData.fc_info.zone_sfid || ves[0]?.zone,
            zone_code: fcData.fc_info.zone_code
        })
        selectVeVillageHandler(standaloneVillages)
        navigate(`/fc/village/${village.sf_id}/profile`)
    }

    const searchEventAnalyticsHandler = (activeTab: string) => {
        trackEvent({
            category: constants.ANALYTICS.SEARCHES,
            action: `fc_searched_${activeTab}`,
            name: constants.ANALYTICS.FC_SEARCHES
        })
    }

    return (
        <div>
            <div className="sticky-header" id="sticky-header">
                <TopBar color={colors.black}>
                    {t('zone')} {fcData.fc_info.zone_name}
                </TopBar>
            </div>
            <div className="page-wrapper">
                <SearchableTab
                    tabs={[
                        {
                            text: t('village.villages'),
                            placeholderText: t(
                                'village.villageSearchPlaceholder'
                            ),
                            noResultsText: t('village.villageNoResults'),
                            icon: (
                                <VillagesIcon
                                    width="21"
                                    height="21"
                                    color={colors.grey}
                                />
                            ),
                            extraClass: 'green',
                            items: villages,
                            renderItem: (village) => (
                                <DownloadVillageCard
                                    village={village as VillageType}
                                    openClickHandler={villageOpenClickHandler}
                                    downloadErrorMsg={t('downloadErrorText')}
                                />
                            ),
                            searchItems: (villages, query) =>
                                searchBy(
                                    villages as VillageType[],
                                    ['name', 'code', 'ves'],
                                    query
                                )
                        },
                        {
                            text: t('village.ves'),
                            placeholderText: t('village.veSearchPlaceholder'),
                            noResultsText: t('village.veNoResults'),
                            icon: (
                                <VeIcon
                                    width="21"
                                    height="21"
                                    color={colors.grey}
                                />
                            ),
                            extraClass: 'blue',
                            items: ves,
                            renderItem: (ve) => (
                                <DownloadVeCard
                                    ve={ve as VeType}
                                    openClickHandler={veOpenClickHandler}
                                    downloadErrorMsg={t('downloadErrorText')}
                                />
                            ),
                            searchItems: (ves, query) =>
                                searchBy(
                                    ves as VeType[],
                                    ['name', 'code', 'current_villages'],
                                    query
                                )
                        }
                    ]}
                    searchAnalyticsEventHandler={searchEventAnalyticsHandler}
                />
            </div>
        </div>
    )
}
